// The competitive dashboard, organised into tabs after the Digipanel reference.
//
// Overview is the reference's first screen: Digital Overview (share and monthly trend, for
// spend and impressions), Spend & Impressions by Platform, and Top Performing Campaigns.
// Spend and impressions are shown together rather than behind a toggle — the deck puts them
// side by side, and comparing a brand's share of spend against its share of impressions is
// the point of that pairing.
//
// Everything plotted is Statex, calibrated where a run exists, across the client AND its
// competitors. Actual spend is never a series: it exists only for the client's own brands,
// so charting it beside competitors would set a measured line against estimated ones. It is
// the input that corrects Statex, nothing more.

import { useState, useEffect, useMemo } from 'react';
import { cmApi } from '../api.js';
import { useClient } from '../ClientContext.jsx';
import { fmtCurrency, fmtNumber } from '../format.js';
import SubTabs from '../components/SubTabs.jsx';
import DonutChart from '../components/DonutChart.jsx';
import StackedBarChart from '../components/StackedBarChart.jsx';
import { Band, Panel, inkAlpha } from '../components/dashboard/Band.jsx';
import { pivot } from '../components/dashboard/pivot.js';
import CreativeOverview from '../components/dashboard/CreativeOverview.jsx';
import OfflineOverview from '../components/dashboard/OfflineOverview.jsx';
import { T, SERIES as PALETTE } from '../../../shared/styles/tokens.js';

// Digital Overview is everything below: the Statex data this module imports is
// publisher-level digital (Facebook, Instagram, Tiktok, Youtube, Ad Networks), so naming the
// tab for it is accurate rather than cosmetic. Offline Overview reads a different dataset
// entirely — TV GRPs, spot durations, programmes, OOH/radio/print media — and is a separate
// page rather than another breakdown of the same rows.
const DASH_TABS = [
  { id: 'digital', label: 'Digital Overview' },
  { id: 'offline', label: 'Offline Overview' },
  { id: 'creative', label: 'Creative' },
];

const BREAKDOWNS = [
  { id: 'brand', label: 'Brand' },
  { id: 'category', label: 'Category' },
  { id: 'market', label: 'Market' },
  { id: 'producer', label: 'Producer' },
];

const BASIS_COPY = {
  verified:  ['Verified',  'o-ok',   'matched to your own actual spend'],
  estimated: ['Estimated', 'o-grey', 'a calibration factor was applied'],
  raw:       ['Raw',       'o-err',  'no factor resolved — unadjusted'],
};

const monthLabel = (m) => {
  if (!m) return '—';
  return new Date(m).toLocaleDateString(undefined, { month: 'short', year: 'numeric' });
};

/**
 * Campaigns grouped as the reference prints them: name once, platform rows beneath.
 *
 * One grand total closes the table rather than a subtotal per campaign — with two or three
 * platforms each, per-group totals interrupted the list more than they informed it. The
 * total is still only trustworthy because the backend limits by campaign rather than by
 * joined row; limiting rows would silently sum whichever survived the cut.
 */
function CampaignTable({ rows }) {
  const groups = useMemo(() => {
    const by = new Map();
    for (const r of rows || []) {
      const g = by.get(r.campaign) || { campaign: r.campaign, brand: r.brand, rows: [], spend: 0, impressions: 0 };
      g.rows.push(r);
      g.spend += Number(r.spend || 0);
      g.impressions += Number(r.impressions || 0);
      by.set(r.campaign, g);
    }
    return [...by.values()]
      .map(g => ({ ...g, rows: g.rows.sort((a, z) => Number(z.spend) - Number(a.spend)) }))
      .sort((a, z) => z.spend - a.spend);
  }, [rows]);

  const total = useMemo(() => (rows || []).reduce(
    (acc, r) => ({
      spend: acc.spend + Number(r.spend || 0),
      impressions: acc.impressions + Number(r.impressions || 0),
    }),
    { spend: 0, impressions: 0 },
  ), [rows]);

  const max = Math.max(...(rows || []).map(r => Number(r.spend) || 0), 1);
  if (!groups.length) return <p className="o-state">No campaigns for the current selection.</p>;

  return (
    <div style={{ overflowX: 'auto' }}>
      <table className="o-tbl">
        <thead>
          <tr>
            <th>Campaign</th><th>Brand</th><th>Platform</th>
            <th style={{ textAlign: 'right' }}>Impressions</th>
            <th style={{ textAlign: 'right' }}>Spend</th>
          </tr>
        </thead>
        <tbody>
          {groups.map(g => g.rows.map((r, i) => (
            <tr key={`${g.campaign}|${r.brand}|${r.channel}`}>
              {/* Name and brand print once per group, as the deck does. */}
              <td className="o-strong">{i === 0 ? g.campaign : ''}</td>
              <td style={{ color: T.text3 }}>{i === 0 ? g.brand : ''}</td>
              <td>{r.channel}</td>
              <td className="o-num" style={{ textAlign: 'right' }}>{fmtNumber(r.impressions)}</td>
              <td className="o-num" style={{
                textAlign: 'right',
                background: inkAlpha((0.04 + (Number(r.spend) / max) * 0.14).toFixed(3)),
              }}>{fmtCurrency(r.spend)}</td>
            </tr>
          )))}
        </tbody>
        <tfoot>
          {/* One total for the table. Per-campaign subtotals interrupted a list of two- and
              three-row groups more than they informed it. */}
          <tr style={{ background: T.surface2, borderTop: `2px solid ${T.rule}` }}>
            <td colSpan={3} className="o-strong">
              Total · {groups.length} campaign{groups.length === 1 ? '' : 's'}
            </td>
            <td className="o-num o-strong" style={{ textAlign: 'right' }}>{fmtNumber(total.impressions)}</td>
            <td className="o-num o-strong" style={{ textAlign: 'right' }}>{fmtCurrency(total.spend)}</td>
          </tr>
        </tfoot>
      </table>
    </div>
  );
}

export default function DashboardPage() {
  const { clientId, clients } = useClient();
  const [tab, setTab] = useState('digital');
  const [filters, setFilters] = useState({
    category: '', brand: '', market: '', channel: '', format: '',
    month_from: '', month_to: '',
  });
  const [breakdown, setBreakdown] = useState('brand');
  const [calibrated, setCalibrated] = useState(true);
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState(null);

  useEffect(() => {
    if (!clientId) return;
    setLoading(true); setErr(null);
    const params = Object.fromEntries(Object.entries(filters).filter(([, v]) => v));
    cmApi.getOverview(clientId, {
      ...params, breakdown, calibrated: calibrated ? 'true' : 'false',
    })
      .then(setData)
      .catch(e => setErr(e.message))
      .finally(() => setLoading(false));
  }, [clientId, filters, breakdown, calibrated]);

  useEffect(() => {
    setFilters({ category: '', brand: '', market: '', channel: '', format: '',
                 month_from: '', month_to: '' });
  }, [clientId]);

  // One colour per member, shared by every chart on the page. Each chart previously indexed
  // the palette by its own ordering, so a brand ranked first in the donut and third in the
  // bars drew in two different colours — the reader then matches the wrong series to the
  // wrong name. Keyed off `share`, which is ordered by spend and covers every member.
  const colors = useMemo(() => {
    const map = {};
    (data?.share || []).forEach((r, i) => { map[r.label] = PALETTE[i % PALETTE.length]; });
    // Anything only the trend series knows about (the rolled-up "Other" bucket) still needs
    // a colour, taken from the end of the palette so it cannot collide with a named member.
    let extra = (data?.share || []).length;
    for (const r of data?.monthly || []) {
      if (!map[r.k]) map[r.k] = PALETTE[extra++ % PALETTE.length];
    }
    return map;
  }, [data]);

  const monthlySpend = useMemo(
    () => pivot(data?.monthly, 'month', 'k', 'spend', monthLabel), [data]);
  const monthlyImpr = useMemo(
    () => pivot(data?.monthly, 'month', 'k', 'impressions', monthLabel), [data]);
  const channelSpend = useMemo(
    () => pivot(data?.by_channel, 'bucket', 'series', 'spend'), [data]);
  const channelImpr = useMemo(
    () => pivot(data?.by_channel, 'bucket', 'series', 'impressions'), [data]);

  if (!clientId) return <p className="o-state">Select a client to view the dashboard.</p>;

  const o = data?.options || {};
  const k = data?.kpis || {};
  const clientName = clients?.find(c => String(c.id) === String(clientId))?.name || '';
  const dimLabel = BREAKDOWNS.find(b => b.id === breakdown)?.label.toLowerCase() || 'brand';

  const select = (key, label, options) => (
    <div className="o-ctrl">
      <label className="o-field-lbl">{label}</label>
      <select className="o-control" value={filters[key]}
        onChange={e => setFilters(f => ({ ...f, [key]: e.target.value }))}>
        <option value="">All</option>
        {(options || []).map(v => <option key={v} value={v}>{v}</option>)}
      </select>
    </div>
  );

  return (
    <div style={{ padding: `0 0 ${T.s7}px` }}>
      <div className="o-head-row" style={{ marginBottom: T.s4 }}>
        <div>
          <p className="o-eyebrow">Competitive intelligence</p>
          <h1 className="o-title">{clientName || 'Dashboard'}</h1>
        </div>
        <div style={{ textAlign: 'right', fontSize: T.tSmall, color: T.text3 }}>
          <div>Latest data · <strong style={{ color: T.text }}>{monthLabel(k.latest_month)}</strong></div>
          <div>{data?.calibrated ? <>{data.calibration_basis === 'mixed' ? 'Calibrated and raw' : 'Calibrated'} · run #{data.run_id}</> : <>Raw Statex · not calibrated</>}</div>
        </div>
      </div>

      <SubTabs tabs={DASH_TABS} active={tab} onChange={setTab} />

      {tab === 'offline' && <OfflineOverview filters={filters} calibrated={calibrated} />}
      {tab === 'creative' && <CreativeOverview filters={filters} calibrated={calibrated} />}

      {tab === 'digital' && <>
      {/* The filter bar belongs to this tab, not the page: its dimensions (creative format,
          platform) are digital Statex columns and mean nothing to the offline dataset. */}
      <div className="o-panel" style={{ padding: `${T.s3}px ${T.s5}px`, marginBottom: T.s5 }}>
        <div className="o-ctrl-row" style={{ padding: 0, border: 0, marginBottom: 0, gap: T.s5 }}>
          {select('category', 'Category', o.categories)}
          {select('brand', 'Brand', o.brands)}
          {select('market', 'Market', o.markets)}
          {select('channel', 'Channel', o.channels)}
          {select('format', 'Creative format', o.formats)}
          <div className="o-ctrl">
            <label className="o-field-lbl">Break down by</label>
            <select className="o-control" value={breakdown}
              onChange={e => setBreakdown(e.target.value)}>
              {BREAKDOWNS.map(b => <option key={b.id} value={b.id}>{b.label}</option>)}
            </select>
          </div>
          <div className="o-ctrl" style={{ justifyContent: 'flex-end' }}>
            <label className="o-field-lbl">Figures</label>
            <div className="o-toggle">
              <button type="button" aria-selected={calibrated} onClick={() => setCalibrated(true)}>Calibrated</button>
              <button type="button" aria-selected={!calibrated} onClick={() => setCalibrated(false)}>Raw</button>
            </div>
          </div>
        </div>
      </div>

      {err && <div className="o-state o-error">{err}</div>}
      {loading && <p className="o-state">Loading…</p>}

      {!loading && data && (
        <>
          <div className="o-stats" style={{
            gridTemplateColumns: 'repeat(auto-fit,minmax(160px,1fr))', marginBottom: T.s5,
          }}>
            <div className="o-stat"><b>{fmtCurrency(k.spend)}</b><span>Total spend</span></div>
            <div className="o-stat"><b>{fmtNumber(k.impressions)}</b><span>Impressions</span></div>
            <div className="o-stat"><b>{k.brands}</b><span>Advertisers</span></div>
            <div className="o-stat"><b>{k.channels}</b><span>Platforms</span></div>
            <div className="o-stat"><b>{k.months}</b><span>Months</span></div>
          </div>

          {/* Kept even though the Methodology section is gone: without it an estimated
              total reads exactly like a measured one. */}
          {calibrated && data.basis_split && (
            <div className="o-panel" style={{ padding: `${T.s3}px ${T.s5}px`, marginBottom: T.s5 }}>
              <p className="o-eyebrow" style={{ marginBottom: T.s2 }}>How this total was arrived at</p>
              <div style={{ display: 'flex', gap: T.s6, flexWrap: 'wrap' }}>
                {data.basis_split.map(b => {
                  const [label, tone, hint] = BASIS_COPY[b.basis] || [b.basis, 'o-grey', ''];
                  return (
                    <div key={b.basis}>
                      <span className={`o-badge ${tone}`}>{label}</span>
                      <div style={{ fontSize: 15, fontWeight: 600, marginTop: 4 }}>{fmtCurrency(b.spend)}</div>
                      <div style={{ fontSize: 11, color: T.text3 }}>
                        {Number(b.records).toLocaleString()} records · {hint}
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>
          )}

          <Band title="Digital Overview" sub={`Share and monthly trend by ${dimLabel}`}>
            {/* The donut column is wider than the trend beside it: labels sit outside the
                ring, so it needs the room more than the bars do. */}
            <div className="cm-chart-pair" style={{ marginBottom: T.s4 }}>
              <Panel title="Share of spend">
                <DonutChart format="currency"
                  segments={(data.share || []).slice(0, 8).map(r => ({
                    label: r.label, value: Number(r.spend || 0), color: colors[r.label],
                  }))} />
              </Panel>
              <Panel title="Monthly spend">
                <StackedBarChart data={monthlySpend.rows} series={monthlySpend.keys}
                  colors={colors} format="currency" height={170} />
              </Panel>
            </div>
            <div className="cm-chart-pair">
              <Panel title="Share of impressions">
                <DonutChart
                  segments={(data.share || []).slice(0, 8).map(r => ({
                    label: r.label, value: Number(r.impressions || 0), color: colors[r.label],
                  }))} />
              </Panel>
              <Panel title="Monthly impressions">
                <StackedBarChart data={monthlyImpr.rows} series={monthlyImpr.keys}
                  colors={colors} format="number" height={170} />
              </Panel>
            </div>
          </Band>

          <Band title="Spend & Impressions by Platform" sub={`Statex channels, stacked by ${dimLabel}`}>
            <div style={{ display: 'grid', gap: T.s4 }}>
              <Panel title="Spend by platform">
                <StackedBarChart data={channelSpend.rows} series={channelSpend.keys}
                  colors={colors} orientation="horizontal" format="currency" barSize={34} />
              </Panel>
              <Panel title="Impressions by platform">
                <StackedBarChart data={channelImpr.rows} series={channelImpr.keys}
                  colors={colors} orientation="horizontal" format="number" barSize={34} />
              </Panel>
            </div>
          </Band>

          <Band title="Top Performing Campaigns" sub="Top 12 campaigns, every platform each ran on">
            <Panel>
              <CampaignTable rows={data.campaigns} />
            </Panel>
          </Band>

        </>
      )}
      </>}
    </div>
  );
}
